import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "redux/actions/auth";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import cosmaLogo from "assets/images/logos/Cosmaupload.png";
import { useMaterialUIController, setLayout } from "context";

const ACCENT = "#1a7f5a";

const S = {
  root: {
    fontFamily: "'Inter', sans-serif",
    background: `url(${bgImage}) center center / cover no-repeat`,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
  },
  box: {
    background: "#fff",
    borderRadius: "24px",
    padding: "48px 40px",
    boxShadow: "0 2px 24px rgba(26,127,90,0.12)",
    width: "100%",
    maxWidth: "520px",
  },
  back: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    color: ACCENT, fontSize: "0.83rem", fontWeight: 600,
    cursor: "pointer", border: "none", background: "none",
    fontFamily: "'Inter', sans-serif", marginBottom: "20px", padding: 0,
  },
  logo: { textAlign: "center", marginBottom: "20px" },
  logoImg: { height: "40px" },
  typeBadge: {
    display: "inline-block", background: `${ACCENT}15`,
    border: `1.5px solid ${ACCENT}`, color: ACCENT,
    fontSize: "0.72rem", fontWeight: 800, padding: "3px 12px",
    borderRadius: "100px", textTransform: "uppercase",
    letterSpacing: "0.08em", marginBottom: "12px",
  },
  h3: { fontSize: "1.55rem", fontWeight: 800, color: "#1a0a2e", margin: "0 0 6px 0" },
  sub: { color: "#7a6895", fontSize: "0.87rem", marginBottom: "24px", lineHeight: 1.6 },
  fieldWrap: { marginBottom: "16px" },
  label: {
    display: "block", fontSize: "0.76rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".06em",
    color: "#7a6895", marginBottom: "6px",
  },
  input: (focus) => ({
    width: "100%", padding: "13px 16px", background: "#f0faf6",
    border: `1.5px solid ${focus ? ACCENT : "rgba(26,127,90,0.18)"}`,
    borderRadius: "10px", color: "#1a0a2e", fontSize: "0.95rem",
    fontFamily: "'Inter', sans-serif", outline: "none",
    boxSizing: "border-box", transition: "border-color .2s",
  }),
  row: { display: "flex", gap: "12px" },
  divider: {
    border: "none", borderTop: "1px solid rgba(26,127,90,0.15)",
    margin: "20px 0",
  },
  sectionTitle: {
    fontSize: "0.78rem", fontWeight: 800, color: ACCENT,
    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "14px",
  },
  btn: {
    width: "100%", padding: "15px", borderRadius: "10px",
    fontSize: "1rem", fontWeight: 800, cursor: "pointer",
    border: "none", marginTop: "8px",
    background: `linear-gradient(135deg, ${ACCENT}, #0d5c40)`, color: "#fff",
    boxShadow: `0 4px 20px ${ACCENT}44`, fontFamily: "'Inter', sans-serif",
    transition: "opacity .2s",
  },
  infoBox: {
    background: `${ACCENT}0f`, border: `1.5px solid ${ACCENT}30`,
    borderRadius: "10px", padding: "12px 16px", marginBottom: "22px",
    fontSize: "0.81rem", color: "#155a3e", lineHeight: 1.6,
  },
  links: { marginTop: "20px", fontSize: "0.83rem", color: "#7a6895", textAlign: "center" },
  a: { color: ACCENT, textDecoration: "none", fontWeight: 600 },
  hint: { fontSize: "0.76rem", color: "#9a88b5", marginTop: "4px" },
  toast: {
    position: "fixed", bottom: "32px", left: "50%", transform: "translateX(-50%)",
    background: `linear-gradient(135deg, ${ACCENT}, #0d5c40)`, color: "#fff",
    padding: "12px 28px", borderRadius: "100px", fontWeight: 700,
    fontSize: "0.9rem", zIndex: 9999, whiteSpace: "nowrap",
  },
};

function OrganisationRegister() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, uiDispatch] = useMaterialUIController();
  const { registerLoading } = useSelector((state) => state.auth);
  const [focused, setFocused] = useState(null);
  const [toast, setToast] = useState({ msg: "", show: false });
  const [pwError, setPwError] = useState("");
  const [form, setForm] = useState({
    orgName: "", adminName: "", email: "", phone: "", password: "", confirmPassword: "",
  });

  useEffect(() => { setLayout(uiDispatch, "page"); }, []);

  const showToast = (msg) => {
    setToast({ msg, show: true });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (e.target.name === "confirmPassword" || e.target.name === "password") setPwError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setPwError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setPwError("Password must be at least 8 characters.");
      return;
    }
    showToast("Registering organisation…");
    dispatch(actions.register({ name: form.adminName, email: form.email, password: form.password, orgName: form.orgName, type: "organisation" }));
  };

  const f = (name) => ({ onFocus: () => setFocused(name), onBlur: () => setFocused(null) });

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={S.root}>
        <div style={S.box}>
          <button style={S.back} onClick={() => navigate("/auth/register")}>
            ← Back to account types
          </button>

          <div style={S.logo}>
            <img src={cosmaLogo} alt="COSMA" style={S.logoImg} />
          </div>

          <span style={S.typeBadge}>Organisation</span>
          <h3 style={S.h3}>Register Your Organisation</h3>
          <p style={S.sub}>
            Set up your organisation to manage doctors, patients and members — all under one admin account.
          </p>

          <div style={S.infoBox}>
            ✓ &nbsp;Multi-user management &nbsp;·&nbsp; ✓ &nbsp;Doctor & patient portals &nbsp;·&nbsp; ✓ &nbsp;Organisation-wide reporting
          </div>

          <div style={{
            background: "#fff8e1", border: "1.5px solid #f9a825",
            borderRadius: "10px", padding: "14px 16px", marginBottom: "22px",
            fontSize: "0.83rem", color: "#6d4c00", lineHeight: 1.6,
          }}>
            <strong>⚠ Subscription required.</strong> Your organisation must have an active COSMA subscription before registering here. Register using the <strong>same email address</strong> linked to your Stripe purchase.{" "}
            <a
              href="https://cosmaapp.com/#pricing"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1a7f5a", fontWeight: 700 }}
            >
              View plans →
            </a>
          </div>

          <form onSubmit={handleSubmit}>
            <p style={S.sectionTitle}>Organisation Details</p>

            <div style={S.fieldWrap}>
              <label style={S.label}>Organisation Name</label>
              <input
                type="text" name="orgName" placeholder="e.g. Sunrise Care Home" required
                value={form.orgName} onChange={handleChange}
                style={S.input(focused === "orgName")} {...f("orgName")}
              />
            </div>

            <hr style={S.divider} />
            <p style={S.sectionTitle}>Admin Account</p>

            <div style={S.fieldWrap}>
              <label style={S.label}>Admin Full Name</label>
              <input
                type="text" name="adminName" placeholder="Your full name" required
                value={form.adminName} onChange={handleChange}
                style={S.input(focused === "adminName")} {...f("adminName")}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Admin Email Address</label>
              <input
                type="email" name="email" placeholder="admin@organisation.com" required
                value={form.email} onChange={handleChange}
                style={S.input(focused === "email")} {...f("email")}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Password</label>
              <input
                type="password" name="password" placeholder="Min. 8 characters" required
                value={form.password} onChange={handleChange}
                style={S.input(focused === "password")} {...f("password")}
              />
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Confirm Password</label>
              <input
                type="password" name="confirmPassword" placeholder="Re-enter password" required
                value={form.confirmPassword} onChange={handleChange}
                style={S.input(focused === "confirmPassword")} {...f("confirmPassword")}
              />
              {pwError && <p style={{ ...S.hint, color: "#c0392b" }}>{pwError}</p>}
            </div>

            <button type="submit" disabled={registerLoading} style={{ ...S.btn, opacity: registerLoading ? 0.7 : 1 }}>
              {registerLoading ? "Registering…" : "Register Organisation →"}
            </button>
          </form>

          <p style={S.links}>
            Already have an account?{" "}
            <a href="/auth/login" style={S.a}>Sign In →</a>
            &nbsp;|&nbsp;
            <a href="/auth/register" style={S.a}>Choose different type</a>
          </p>
        </div>
      </div>
      {toast.show && <div style={S.toast}>{toast.msg}</div>}
    </>
  );
}

export default OrganisationRegister;
